import { useEffect, useState } from "react";
import { CalendarClock, Trash2, Phone, Ban, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const Appointments = () => {
  const { lang, t } = useLang();
  const a = t.dash.appointments;
  const [items, setItems] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [vacUntil, setVacUntil] = useState("");
  const [vacActive, setVacActive] = useState("");

  useEffect(() => {
    api.get("/appointments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/appointments/blocked").then((r) => setBlocks(r.data)).catch(() => {});
    api.get("/vacation").then((r) => setVacActive(r.data.until)).catch(() => {});
  }, []);

  const saveVacation = async (until) => {
    await api.put("/settings/vacation", { until });
    setVacActive(until);
    setVacUntil("");
  };

  const remove = async (id) => {
    await api.delete(`/appointments/${id}`);
    setItems((xs) => xs.filter((x) => x.id !== id));
  };

  const addBlock = async () => {
    if (!date) return;
    const key = hour ? `${date}T${hour}` : date;
    const res = await api.post("/appointments/blocked", { key });
    setBlocks((bs) => (bs.some((b) => b.key === key) ? bs : [...bs, { key: res.data.key }].sort((x, y) => x.key.localeCompare(y.key))));
    setDate("");
    setHour("");
  };

  const toggleWeekly = async (i) => {
    const key = `weekly:${i}`;
    if (blocks.some((b) => b.key === key)) {
      await api.delete(`/appointments/blocked/${key}`);
      setBlocks((bs) => bs.filter((b) => b.key !== key));
    } else {
      await api.post("/appointments/blocked", { key });
      setBlocks((bs) => [...bs, { key }]);
    }
  };

  const removeBlock = async (key) => {
    await api.delete(`/appointments/blocked/${key}`);
    setBlocks((bs) => bs.filter((b) => b.key !== key));
  };

  const fmt = (s) =>
    new Date(s).toLocaleString(lang === "fr" ? "fr-BE" : "en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="bg-[#111111] border border-[#FF7900]/25 p-6 md:p-8 mb-10" data-testid="appointments-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-5">
        <CalendarClock className="w-4 h-4 text-[#FF7900]" /> {a.title} ({items.length})
      </p>
      {items.length === 0 && <p className="text-xs text-white/30 mb-4">{a.empty}</p>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((x) => (
          <div key={x.id} className="border border-white/10 bg-[#0a0a0a] p-4 flex items-start justify-between gap-3" data-testid={`appointment-${x.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{x.name}</p>
              <p className="text-[#FF7900] text-sm font-semibold mt-0.5">{fmt(x.slot)}</p>
              <a href={`tel:${x.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-[#FF7900] transition-colors duration-300">
                <Phone className="w-3 h-3" /> {x.phone}
              </a>
            </div>
            <button
              onClick={() => remove(x.id)}
              data-testid={`appointment-delete-${x.id}`}
              aria-label={a.delete}
              className="text-white/25 hover:text-red-400 transition-colors duration-300 shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-5 border-t border-white/10" data-testid="vacation-mode">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/30 mb-3">
          <CalendarClock className="w-3.5 h-3.5 text-amber-400" /> {a.vacationTitle}
        </p>
        {vacActive ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-amber-400" data-testid="vacation-status">{a.vacationOn} {vacActive}</span>
            <button
              onClick={() => saveVacation("")}
              data-testid="vacation-off-button"
              className="text-xs border border-white/15 hover:border-amber-400 hover:text-amber-400 px-4 py-1.5 rounded-full transition-colors duration-300"
            >
              {a.vacationDisable}
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={vacUntil}
              onChange={(e) => setVacUntil(e.target.value)}
              data-testid="vacation-date-input"
              className="bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300 [color-scheme:dark]"
            />
            <button
              onClick={() => saveVacation(vacUntil)}
              disabled={!vacUntil}
              data-testid="vacation-on-button"
              className="text-xs border border-white/15 hover:border-amber-400 hover:text-amber-400 disabled:opacity-40 px-4 py-1.5 rounded-full transition-colors duration-300"
            >
              {a.vacationEnable}
            </button>
            <span className="text-[11px] text-white/30">{a.vacationHint}</span>
          </div>
        )}
      </div>
      <div className="mt-6 pt-5 border-t border-white/10" data-testid="busy-blocker">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/30 mb-3">
          <Ban className="w-3.5 h-3.5 text-red-400" /> {a.blockTitle}
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[11px] text-white/40 mr-1">{a.blockWeekly}</span>
          {a.weekdays.map((w, i) => (
            <button
              key={w}
              onClick={() => toggleWeekly(i)}
              data-testid={`block-weekly-${i}`}
              className={`px-3 py-1 text-[11px] font-semibold rounded-full border transition-colors duration-300 ${
                blocks.some((b) => b.key === `weekly:${i}`)
                  ? "border-red-400 bg-red-500/15 text-red-300"
                  : "border-white/15 text-white/50 hover:border-white/40"
              }`}
            >
              {w}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="block-date-input"
            className="bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300 [color-scheme:dark]"
          />
          <select
            value={hour}
            onChange={(e) => setHour(e.target.value)}
            data-testid="block-hour-select"
            className="bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300 cursor-pointer"
          >
            <option value="" className="bg-[#111]">{a.blockAllDay}</option>
            {["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"].map((h) => (
              <option key={h} value={h} className="bg-[#111]">{h}</option>
            ))}
          </select>
          <button
            onClick={addBlock}
            disabled={!date}
            data-testid="block-add-button"
            className="text-xs border border-white/15 hover:border-red-400 hover:text-red-400 disabled:opacity-40 px-4 py-1.5 rounded-full transition-colors duration-300"
          >
            {a.blockAdd}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {blocks.filter((b) => !b.key.startsWith("weekly:")).map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1.5 border border-red-500/30 bg-red-500/10 text-red-300 px-2.5 py-1 rounded-full text-[11px]" data-testid={`block-chip-${b.key}`}>
              {b.key.replace("T", " · ")}
              <button onClick={() => removeBlock(b.key)} aria-label={a.blockRemove} data-testid={`block-remove-${b.key}`} className="hover:text-white transition-colors duration-200">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
